import { useCallback, useEffect, useRef, useState } from "react";

import { useSession } from "../../session/SessionContext.js";
import { AvatarField } from "./AvatarField.js";
import { messageFor } from "./ProfilePanel.js";

/**
 * What the assistant is called, and what it looks like.
 *
 * Held by the platform rather than in the shop's WordPress settings, because the
 * widget is no longer the only place the assistant appears — the dashboard labels
 * its messages, the app does, and so do push notifications. A value that only
 * WordPress knows is unreachable from three of those four, which is how they came
 * to disagree with each other.
 */

interface Identity {
  readonly displayName: string;
  readonly avatarUrl?: string;
}

export function AssistantIdentity() {
  const { api } = useSession();

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);

  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (savedTimer.current !== null) clearTimeout(savedTimer.current);
    },
    [],
  );

  const adopt = useCallback((next: Identity) => {
    setIdentity(next);
    setDisplayName(next.displayName);
  }, []);

  useEffect(() => {
    let live = true;

    void api
      .get<Identity>("/assistant/identity")
      .then((loaded) => {
        if (live) adopt(loaded);
      })
      .catch(() => {
        if (live) setError("The assistant's details could not be loaded.");
      });

    return () => {
      live = false;
    };
  }, [api, adopt]);

  async function save(): Promise<void> {
    setError(null);
    setState("saving");

    try {
      adopt(await api.put<Identity>("/admin/assistant/identity", { displayName }));
      setState("saved");

      if (savedTimer.current !== null) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => {
        setState("idle");
      }, 2_500);
    } catch (cause) {
      setState("idle");
      setError(messageFor(cause, "That could not be saved."));
    }
  }

  if (identity === null) {
    return error === null ? <p className="muted">Loading…</p> : <p className="error">{error}</p>;
  }

  return (
    <>
      <section className="card">
        <h2>The assistant&rsquo;s photo</h2>
        <p className="hint">
          Shown beside every message the assistant sends, in the chat window, the dashboard and the
          app. Leave it empty and its initial is drawn instead.
        </p>

        <AvatarField
          name={identity.displayName}
          avatarUrl={identity.avatarUrl}
          uploadPath="/admin/assistant/avatar"
          deletePath="/admin/assistant/avatar"
          onChanged={(updated) => {
            adopt(updated as Identity);
          }}
        />
      </section>

      <section className="card">
        <h2>The assistant&rsquo;s name</h2>

        <label>
          Display name
          <input
            value={displayName}
            maxLength={60}
            onChange={(event) => {
              setDisplayName(event.target.value);
            }}
          />
          <span className="hint">
            What a customer sees above the assistant&rsquo;s replies. Everywhere the assistant
            appears reads this one value.
          </span>
        </label>

        {error !== null && <p className="error">{error}</p>}

        <div className="row">
          <button
            className="primary"
            disabled={
              state === "saving" ||
              displayName.trim() === "" ||
              displayName === identity.displayName
            }
            onClick={() => void save()}
          >
            {state === "saving" ? "Saving…" : "Save name"}
          </button>
          {state === "saved" && <span className="small muted">Saved.</span>}
        </div>
      </section>
    </>
  );
}
